import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import type { ActivityLog, ActivityLogFilters, ActivityLogPage } from '../../services/logactivityService.ts';
import type { SecurityLog, SecurityLogFilters, SecurityLogPage } from '../../services/logsecurityService.ts';

const PAGE_SIZE = 10;
type Kind = 'activity' | 'security';
type LogRow = ActivityLog | SecurityLog;
type Filters = ActivityLogFilters & SecurityLogFilters;
type LogPage = ActivityLogPage | SecurityLogPage;

const userLabel = (row: LogRow) => {
  if (!row.user) return 'Sistema';
  return `${row.user.first_name || ''} ${row.user.last_name || ''}`.trim() || row.user.email;
};

const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#007FC8] focus:ring-2 focus:ring-[#007FC8]/15';

const AuditLogList: React.FC<{ kind: Kind; loadLogs: (filters: Filters) => Promise<LogPage> }> = ({ kind, loadLogs }) => {
  const activity = kind === 'activity';
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [count, setCount] = useState(0);
  const [paginated, setPaginated] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const params = { ...filters, page, page_size: PAGE_SIZE } as Filters;
    loadLogs(params).then(data => {
      if (!active) return;
      setLogs(data.results);
      setCount(data.count);
      setPaginated(data.paginated);
      setLoading(false);
    }).catch(() => {
      if (active) {
        setError(`No pudimos cargar el historial de ${activity ? 'actividad' : 'seguridad'}.`);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, [activity, filters, loadLogs, page, reload]);

  const displayedLogs = useMemo(
    () => paginated ? logs : logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [logs, page, paginated],
  );
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (draft.fecha_desde && draft.fecha_hasta && draft.fecha_desde > draft.fecha_hasta) {
      setValidationError('La fecha desde no puede ser posterior a la fecha hasta.');
      return;
    }
    setValidationError('');
    setPage(1);
    setFilters(Object.fromEntries(Object.entries(draft).filter(([, value]) => value.trim())));
  };

  const field = (key: string, label: string, type = 'text') => (
    <label className="block text-xs font-extrabold uppercase tracking-wide text-slate-500">
      {label}
      <input aria-label={label} type={type} value={draft[key] || ''} onChange={event => setDraft(current => ({ ...current, [key]: event.target.value }))} className={`${inputClass} mt-1`} />
    </label>
  );

  return (
    <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,.07)]">
      <form onSubmit={submit} className="border-b border-slate-100 bg-slate-50/70 p-4">
        <div className={`grid gap-3 sm:grid-cols-2 ${activity ? 'lg:grid-cols-5' : 'lg:grid-cols-7'}`}>
          {field('usuario', activity ? 'Usuario, nombre o correo' : 'Usuario o correo')}
          {field('accion', 'Acción')}
          {!activity && field('ip', 'Dirección IP')}
          {!activity && field('resultado', 'Resultado')}
          {field('fecha_desde', 'Fecha desde', 'date')}
          {field('fecha_hasta', 'Fecha hasta', 'date')}
          <div className="flex items-end"><button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#007FC8] px-4 py-2.5 text-sm font-extrabold text-white"><Search size={16} /> Aplicar filtros</button></div>
        </div>
      </form>

      {(validationError || error) && <div role="alert" className="m-4 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"><span className="flex items-center gap-2"><AlertTriangle size={17} />{validationError || error}</span>{error && <button type="button" onClick={() => setReload(value => value + 1)} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 font-bold">Reintentar</button>}</div>}

      {loading ? <div role="status" className="flex min-h-52 items-center justify-center gap-2 text-sm font-bold text-slate-500"><RefreshCw size={18} className="animate-spin text-[#007FC8]" /> Cargando registros de {activity ? 'actividad' : 'seguridad'}…</div> : !error && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead className="bg-slate-50"><tr>{(activity ? ['Usuario', 'Correo', 'Acción', 'Detalle', 'Fecha'] : ['Usuario', 'Correo', 'Acción', 'IP', 'Resultado', 'Fecha']).map(label => <th key={label} className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">{label}</th>)}</tr></thead>
              <tbody>{displayedLogs.length ? displayedLogs.map(row => {
                const security = row as SecurityLog;
                return <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70"><td className="whitespace-nowrap px-4 py-3 font-bold text-slate-800">{userLabel(row)}</td><td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.user?.email || '—'}</td><td className="px-4 py-3 text-slate-700">{row.action}</td>{activity ? <td className="min-w-64 px-4 py-3 text-slate-600">{(row as ActivityLog).mensaje}</td> : <><td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-600">{security.ip_address || '—'}</td><td className="px-4 py-3 text-slate-700">{security.resultado}</td></>}<td className="whitespace-nowrap px-4 py-3 text-slate-500">{new Date(row.fecha).toLocaleString('es-BO', { timeZone: 'America/La_Paz' })}</td></tr>;
              }) : <tr><td colSpan={activity ? 5 : 6} className="px-6 py-14 text-center font-semibold text-slate-500">No encontramos {activity ? 'actividad' : 'eventos de seguridad'} con los filtros seleccionados.</td></tr>}</tbody>
            </table>
          </div>
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-500"><strong className="text-slate-700">{count} {count === 1 ? `registro de ${activity ? 'actividad' : 'seguridad'}` : `registros de ${activity ? 'actividad' : 'seguridad'}`}</strong><div className="flex items-center gap-2"><button type="button" aria-label="Anterior" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))} className="rounded-lg border border-slate-200 p-2 disabled:opacity-30"><ChevronLeft size={16} /></button><span>Página {page} de {totalPages}</span><button type="button" aria-label="Siguiente" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))} className="rounded-lg border border-slate-200 p-2 disabled:opacity-30"><ChevronRight size={16} /></button></div></footer>
        </>
      )}
    </div>
  );
};

export default AuditLogList;
